import React, { createContext, useState, useEffect, useContext } from 'react';

const CART_STORAGE_KEY = 'starryhwa_cart';

const CartContext = createContext();

export const useCart = () => useContext(CartContext);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    try {
      const savedCart = localStorage.getItem(CART_STORAGE_KEY);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (error) {
      console.warn('Unable to load cart from localStorage:', error);
    }
    return [];
  });

  // Persist cart to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cartItems));
    } catch (error) {
      console.warn('Unable to save cart to localStorage:', error);
    }
  }, [cartItems]);

  // Synchronize cart across open browser tabs
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === CART_STORAGE_KEY) {
        try {
          const updated = e.newValue ? JSON.parse(e.newValue) : [];
          if (Array.isArray(updated)) {
            setCartItems(updated);
          }
        } catch (err) {
          console.warn('Error syncing cart from storage event:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  // Mini-Cart Slide-Over Drawer State
  const [isCartOpen, setIsCartOpen] = useState(false);
  const openCart = () => setIsCartOpen(true);
  const closeCart = () => setIsCartOpen(false);
  const toggleCart = () => setIsCartOpen((prev) => !prev);

  const addToCart = (product, selectedOptions = {}, quantity = 1, openDrawer = true) => {
    const qtyToAdd = typeof quantity === 'number' && quantity > 0 ? quantity : 1;
    if (openDrawer) {
      setIsCartOpen(true);
    }
    setCartItems((prevItems) => {
      // Build a unique key for items with customization options
      const hasOptions = Object.keys(selectedOptions).length > 0;
      const cartItemId = hasOptions 
        ? `${product.id}-${selectedOptions.type || ''}-${selectedOptions.color || ''}-${selectedOptions.member || ''}-${selectedOptions.theme || ''}-${selectedOptions.customNote || ''}`
        : `${product.id}`;

      const existingIndex = prevItems.findIndex((item) => item.cartItemId === cartItemId);
      if (existingIndex > -1) {
        const updated = [...prevItems];
        updated[existingIndex] = {
          ...updated[existingIndex],
          quantity: updated[existingIndex].quantity + qtyToAdd
        };
        return updated;
      }
      
      const squareVariationId = product.squareVariationId || `VAR_${product.id}`;

      return [...prevItems, { 
        ...product, 
        squareVariationId,
        cartItemId,
        selectedOptions, 
        quantity: qtyToAdd 
      }];
    });
  };

  const removeFromCart = (cartItemId) => {
    setCartItems((prevItems) => prevItems.filter(item => item.cartItemId !== cartItemId));
  };

  const updateQuantity = (cartItemId, quantity) => {
    setCartItems((prevItems) => 
      prevItems.map((item) => 
        item.cartItemId === cartItemId ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const getCartCount = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const getCartTotal = () => {
    return cartItems.reduce((total, item) => {
      if (typeof item.priceNum === 'number' && !isNaN(item.priceNum)) {
        return total + (item.priceNum * item.quantity);
      }
      // Parse "$7 - $10" -> "7"
      const priceStr = String(item.price || '').split('-')[0];
      const price = parseFloat(priceStr.replace(/[^0-9.]+/g,"")) || 0;
      return total + (price * item.quantity);
    }, 0);
  };

  const clearCart = () => {
    setCartItems([]);
    try {
      localStorage.removeItem(CART_STORAGE_KEY);
    } catch (error) {
      console.warn('Unable to clear cart in localStorage:', error);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cartItems,
        isCartOpen,
        openCart,
        closeCart,
        toggleCart,
        addToCart,
        removeFromCart,
        updateQuantity,
        getCartCount,
        getCartTotal,
        clearCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
